import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { ServerCrash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { PostModel } from '@/common/models/post'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'

async function putNewsActive({ id, ...news }: Omit<PostModel, 'PostsPinned'>) {
  const form = new FormData()

  form.append('body', JSON.stringify(news))

  return api.backend.put(`/posts/${id}`, form)
}

export function useToggleNewsActive() {
  const { t } = useTranslation('panel')

  const { mutateAsync, isPending } = useMutation<
    unknown,
    AxiosError<ApiError>,
    Omit<PostModel, 'PostsPinned'>
  >({
    mutationFn: putNewsActive,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/posts-slider/paginate'] })
      queryClient.invalidateQueries({
        queryKey: ['/posts-slider-inactive/paginate'],
      })
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: ServerCrash,
          title: t('news.use_toggle_news_active.toast_error.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('news.use_toggle_news_active.toast_error.description'),
        },
      })
    },
  })

  const toggleNewsActive = ({ PostsPinned, ...news }: PostModel) => {
    const updatedNews = {
      ...news,
      sliderSort: news?.sliderSort === -1 ? 0 : -1,
      updatedAt: new Date().toISOString(),
    }

    mutateAsync(updatedNews)
  }

  return { toggleNewsActive, isToggling: isPending }
}
